export function DataTable({
  children,
  ariaLabel,
  className = "",
  density = "default",
  minWidth,
}) {
  return (
    <div
      className={`suite-data-table is-${density} ${className}`.trim()}
    >
      <div className="suite-data-table-scroll">
        <table
          aria-label={ariaLabel}
          style={minWidth ? { "--suite-table-min-width": minWidth } : undefined}
        >
          {children}
        </table>
      </div>
    </div>
  );
}

export function DataTableHead({ children }) {
  return <thead>{children}</thead>;
}

export function DataTableBody({ children }) {
  return <tbody>{children}</tbody>;
}

export function DataTableRow({
  children,
  className = "",
  id,
  label,
  onActivate,
  tabIndex,
}) {
  const interactive = typeof onActivate === "function";

  const handleKeyDown = (event) => {
    if (!interactive || event.defaultPrevented) return;
    if (event.key === "Enter") {
      event.preventDefault();
      onActivate();
    }
  };

  return (
    <tr
      id={id}
      className={`${interactive ? "is-interactive" : ""} ${className}`.trim()}
      tabIndex={interactive ? 0 : tabIndex}
      aria-label={interactive ? label : undefined}
      onClick={interactive ? onActivate : undefined}
      onKeyDown={handleKeyDown}
    >
      {children}
    </tr>
  );
}

export function DataTableHeaderCell({
  children,
  align = "start",
  className = "",
  priority,
  scope = "col",
}) {
  return (
    <th
      className={className}
      scope={scope}
      data-align={align}
      data-priority={priority}
    >
      {children}
    </th>
  );
}

export function DataTableCell({
  children,
  align = "start",
  className = "",
  colSpan,
  label,
  priority,
}) {
  return (
    <td
      className={className}
      colSpan={colSpan}
      data-align={align}
      data-label={label}
      data-priority={priority}
    >
      {children}
    </td>
  );
}

export function DataTablePrimaryCell({
  action = null,
  eyebrow,
  title,
  meta,
  icon = null,
  badges = null,
}) {
  return (
    <div className="suite-data-table-primary">
      {icon && <span className="suite-data-table-primary-icon">{icon}</span>}
      <span className="suite-data-table-primary-copy">
        {(eyebrow || badges) && (
          <span className="suite-data-table-primary-overline">
            {eyebrow && <span>{eyebrow}</span>}
            {badges && (
              <span className="suite-data-table-primary-badges">{badges}</span>
            )}
          </span>
        )}
        {action ? (
          <span className="suite-data-table-primary-title">
            <strong>{title}</strong>
            {action}
          </span>
        ) : (
          <strong>{title}</strong>
        )}
        {meta && <small>{meta}</small>}
      </span>
    </div>
  );
}

export function DataTableValue({ primary, secondary }) {
  return (
    <span className="suite-data-table-value">
      <strong>{primary}</strong>
      {secondary && <small>{secondary}</small>}
    </span>
  );
}

export function DataTableBadge({
  children,
  className = "",
  icon = null,
  tone = "neutral",
}) {
  return (
    <span
      className={`suite-data-table-badge is-${tone} ${className}`.trim()}
    >
      {icon}
      {children}
    </span>
  );
}

export function DataTableStatus({
  label,
  description,
  tone = "neutral",
}) {
  return (
    <span className={`suite-data-table-status is-${tone}`}>
      <span className="suite-data-table-status-dot" aria-hidden="true" />
      <span>
        <strong>{label}</strong>
        {description && <small>{description}</small>}
      </span>
    </span>
  );
}

export function DataTableAction({
  children,
  disabled = false,
  icon = null,
  label,
  onClick,
}) {
  return (
    <button
      className="suite-data-table-action"
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={(event) => {
        event.stopPropagation();
        onClick?.(event);
      }}
    >
      {children && (
        <span className="suite-data-table-action-label">{children}</span>
      )}
      {icon}
    </button>
  );
}
